/**
 * Atlas Dock — persistent, minimally intrusive launcher.
 *
 * Collapsed: a 40px pill with a badge count of open missions.
 * Expanded: current mission + progress + Close (hide all Atlas) + mission list.
 *
 * The Dock NEVER duplicates the main Navigation. It lives at a fixed position
 * and hides on auth / onboarding / pricing / solutions routes (handled by
 * Layout's `shouldShowAtlasDock`).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Sparkles, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAtlas } from './AtlasProvider';
import { ATLAS_BRAND, ATLAS_DESCRIPTOR, type AtlasMissionId } from './atlas.types';
import { dockableMissions, isSnoozed } from './chooseCurrentMission';
import { emitAtlas } from './telemetry';
import './atlas-tour.css';

export const AtlasDock: React.FC = () => {
  const atlas = useAtlas();
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const launcherRef = useRef<HTMLButtonElement | null>(null);

  const missions = useMemo(
    () => dockableMissions(atlas.states, atlas.permissionPredicate),
    [atlas.states, atlas.permissionPredicate]
  );

  const actionable = useMemo(
    () => missions.filter((m) => !m.state.completed && !m.state.skipped && !isSnoozed(m.state)),
    [missions]
  );

  const completedCount = useMemo(
    () => missions.filter((m) => m.state.completed).length,
    [missions]
  );

  const { currentMission } = atlas;

  // Hide the dock entirely when there's nothing actionable and the user hasn't
  // opened it before. Anti-noise rule.
  const hasAnything = actionable.length > 0 || currentMission != null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        launcherRef.current?.focus();
      }
    };
    const onClickAway = (e: MouseEvent) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target as Node)) return;
      if (launcherRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClickAway);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClickAway);
    };
  }, [open]);

  const toggle = useCallback(() => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        emitAtlas('atlas.dock.opened', {
          orgId: atlas.ctx.orgId,
          productId: atlas.ctx.productId,
        });
      } else {
        emitAtlas('atlas.dock.closed', {
          orgId: atlas.ctx.orgId,
          productId: atlas.ctx.productId,
        });
      }
      return next;
    });
  }, [atlas.ctx.orgId, atlas.ctx.productId]);

  if (!atlas.ctx.orgId) return null;
  if (atlas.guidanceDismissed) return null;
  if (!hasAnything && !open) return null;
  // Organization is always present inside the app (RequireOrg). Product may be
  // absent on first run — the dock stays visible so the "urgent" launcher can
  // call attention to the missing blocker (no product / no Jira).

  const totalMissions = missions.length;
  const pctComplete = totalMissions === 0 ? 0 : Math.round((completedCount / totalMissions) * 100);
  const isUrgent = currentMission?.critical === true;

  const handleStart = (id: AtlasMissionId) => {
    atlas.startMission(id);
    setOpen(false);
  };

  return (
    <div className="atlas-dock" role="complementary" aria-label={`${ATLAS_BRAND} helper`}>
      {open ? (
        <div
          className="atlas-dock__panel"
          data-theme={theme}
          role="dialog"
          aria-modal="false"
          aria-labelledby="atlas-dock-title"
          ref={panelRef}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={`Close ${ATLAS_BRAND}`}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              opacity: 0.7,
            }}
          >
            <X size={16} />
          </button>
          <div className="atlas-dock__kicker">
            <Sparkles size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {ATLAS_BRAND} — {ATLAS_DESCRIPTOR}
          </div>
          <h3 id="atlas-dock-title" className="atlas-dock__title">
            {currentMission
              ? currentMission.title
              : completedCount === totalMissions
                ? 'All set — you can close this anytime'
                : 'Next thing to try'}
          </h3>
          <p className="atlas-dock__subtitle">
            {currentMission
              ? currentMission.description
              : completedCount === totalMissions
                ? 'Restart any tour from its page whenever you want a refresher.'
                : 'Pick a mission below when you have a minute.'}
          </p>
          <div className="atlas-dock__progress" aria-hidden>
            <div className="atlas-dock__progress-bar" style={{ width: `${pctComplete}%` }} />
          </div>
          <div style={{ fontSize: 12, opacity: 0.75 }}>
            {completedCount} of {totalMissions} complete
          </div>

          <div className="atlas-dock__actions">
            <button
              type="button"
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
              onClick={() => {
                setOpen(false);
                atlas.dismissGuidance();
              }}
              aria-label={`Hide ${ATLAS_BRAND} until you restore it in Profile settings`}
            >
              Close
            </button>
          </div>

          <ul className="atlas-dock__list">
            {missions.map(({ mission, state }) => {
              const snoozed = isSnoozed(state);
              const inProgress =
                !state.completed &&
                !state.skipped &&
                !snoozed &&
                Boolean(state.step) &&
                state.step !== 'completed' &&
                state.step !== 'skipped';
              const notStarted = !state.completed && !state.skipped && !snoozed && !inProgress;
              const label = state.completed
                ? 'Done'
                : state.skipped
                  ? 'Skipped'
                  : snoozed
                    ? 'Snoozed'
                    : inProgress
                      ? 'In progress'
                      : 'Not started';
              const listItemClass = [
                'atlas-dock__list-item',
                state.completed ? 'atlas-dock__list-item--completed' : '',
                notStarted ? 'atlas-dock__list-item--not-started' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const listStatusClass = [
                'atlas-dock__list-status',
                state.completed ? 'atlas-dock__list-status--done' : '',
                notStarted ? 'atlas-dock__list-status--not-started' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const linkClass = [
                'atlas-dock__link',
                state.completed ? 'atlas-dock__link--done' : '',
                notStarted ? 'atlas-dock__link--not-started' : '',
              ]
                .filter(Boolean)
                .join(' ');
              // Derived missions have no steps — completion is observed, not
              // user-driven. Render status as a non-interactive pill so users
              // don't get a Start button that does nothing.
              const isDerived = mission.storage === 'derived';
              return (
                <li key={mission.id} className={listItemClass}>
                  <span className="atlas-dock__list-title" title={mission.title}>
                    {mission.title}
                  </span>
                  <span className={listStatusClass}>
                    {isDerived ? (
                      <span
                        className={linkClass}
                        aria-label={
                          state.completed
                            ? `${mission.title}: completed`
                            : `${mission.title}: ${label.toLowerCase()}`
                        }
                      >
                        {state.completed ? (
                          <>
                            <Check size={12} strokeWidth={2.5} aria-hidden />
                            Done
                          </>
                        ) : (
                          label
                        )}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={linkClass}
                        aria-label={
                          state.completed
                            ? `${mission.title}: completed. Open to restart tour`
                            : undefined
                        }
                        onClick={() => handleStart(mission.id)}
                      >
                        {state.completed ? (
                          <>
                            <Check size={12} strokeWidth={2.5} aria-hidden />
                            Done
                          </>
                        ) : (
                          label
                        )}
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <button
        ref={launcherRef}
        type="button"
        className={`atlas-dock__launcher${isUrgent ? ' atlas-dock__launcher--urgent' : ''}`}
        onClick={toggle}
        aria-expanded={open}
        aria-controls="atlas-dock-title"
        aria-label={
          isUrgent && currentMission
            ? `${ATLAS_BRAND} — action needed: ${currentMission.title}`
            : `${ATLAS_BRAND} — ${actionable.length} mission${actionable.length === 1 ? '' : 's'} open`
        }
        data-urgent={isUrgent ? 'true' : undefined}
      >
        <Sparkles size={14} aria-hidden />
        <span>{ATLAS_BRAND}</span>
        {actionable.length > 0 ? (
          <span className="atlas-dock__launcher-badge">{actionable.length}</span>
        ) : null}
      </button>
    </div>
  );
};
